import { useEffect, useState, useCallback } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { EnvelopeSimple, ArrowsClockwise, CheckCircle, WarningCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import { EmailTestPanel } from "../components/EmailTestPanel";
import { EmailProbeCard } from "../components/EmailProbeCard";
import { EmailLogTable } from "../components/EmailLogTable";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

function Stat({ label, value, tone, testid }) {
  const tones = { ok: "text-emerald-600", bad: "text-red-600", muted: "text-foreground" };
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold ${tones[tone] || tones.muted}`}>{value}</div>
    </div>
  );
}

export default function EmailHealthPage() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/platform/email-health");
      setHealth(data);
    } catch { /* toast handled globally */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const t = health?.transport;
  const d = health?.last_24h;

  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="email-health-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><EnvelopeSimple size={24} weight="duotone" /> E-mail Health</h1>
          <p className="text-sm text-muted-foreground mt-1">Every e-mail the app tries to send is logged here — sent or failed, and why. Use the test panel to push a real sample of each kind to your inbox.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setRefreshKey((k) => k + 1)} data-testid="email-health-refresh"><ArrowsClockwise size={14} className="mr-1" /> Refresh</Button>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : health && (
        <>
          <div className="rounded-lg border border-border bg-card p-4" data-testid="email-transport-card">
            <div className="flex items-center gap-2 flex-wrap text-sm">
              {t.configured ? <Badge className="bg-emerald-100 text-emerald-700" data-testid="email-transport-status"><CheckCircle size={12} className="mr-1" /> Transport configured</Badge>
                : <Badge className="bg-red-100 text-red-700" data-testid="email-transport-status"><WarningCircle size={12} className="mr-1" /> No transport — nothing can be sent</Badge>}
              <span className="text-muted-foreground">Sends as <strong className="text-foreground">{t.sender || "—"}</strong>{t.smtp_host ? <> via <strong className="text-foreground">{t.smtp_host}</strong></> : null} · order: {t.chain.join(" → ") || "none"}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-2">Links in e-mails point to <code className="text-foreground">{t.links_base}</code>{t.is_preview && <span className="text-amber-700 dark:text-amber-400"> · preview workspace — the published app uses your branded domain</span>}</div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Sent · 24 h" value={d.sent} tone="ok" testid="email-stat-sent-24h" />
            <Stat label="Failed · 24 h" value={d.failed} tone={d.failed ? "bad" : "muted"} testid="email-stat-failed-24h" />
            <Stat label="Sent · 7 days" value={health.last_7d.sent} tone="ok" testid="email-stat-sent-7d" />
            <Stat label="Failed · 7 days" value={health.last_7d.failed} tone={health.last_7d.failed ? "bad" : "muted"} testid="email-stat-failed-7d" />
          </div>

          {health.last_failure && (
            <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20 px-4 py-3 text-xs" data-testid="email-last-failure">
              <strong className="text-red-700 dark:text-red-300">Most recent failure</strong> · {fmt(health.last_failure.at)} · to {health.last_failure.to} · {health.last_failure.subject}
              <div className="text-red-800 dark:text-red-200 mt-1">{health.last_failure.error}</div>
            </div>
          )}

          <EmailProbeCard />
          <EmailTestPanel onFinished={() => setRefreshKey((k) => k + 1)} />
          <EmailLogTable refreshKey={refreshKey} />
          <p className="text-[11px] text-muted-foreground flex items-center gap-1"><PaperPlaneTilt size={12} /> "Sent" means the mail server accepted the message. If something accepted here never arrives, check the recipient's spam folder and the sending domain's SPF/DKIM records.</p>
        </>
      )}
    </div>
  );
}
